import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(n: number | null, opts?: { fractionDigits?: number; currency?: string }): string | null {
  if (n == null) return null;
  const fractionDigits = opts?.fractionDigits ?? 0;
  const fixed = Number(n).toFixed(fractionDigits);
  const [intPart, decPart] = fixed.split(".");
  // Insert "." as thousands separator (es-ES). Avoid relying on Intl, which may
  // fall back to default grouping in some serverless runtimes without full ICU.
  const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const formatted = decPart ? `${intWithDots},${decPart}` : intWithDots;
  return formatted + (opts?.currency ? ` ${opts.currency}` : "");
}

/** Convert ISO date (YYYY-MM-DD) to European format (DD/MM/YYYY) for display */
export function formatDateEU(isoDate: string | null | undefined): string {
  if (!isoDate) return "";
  const m = isoDate.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return isoDate;
  return `${m[2]}/${m[1]}/${m[0].slice(0, 4)}`;
}

/** Parse European date (DD/MM/YYYY) to ISO format (YYYY-MM-DD) for storage */
export function parseDateEU(euDate: string): string | null {
  const trimmed = euDate.trim();
  if (!trimmed) return null;
  const m = trimmed.match(/^\s*(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})\s*$/);
  if (!m) return null;
  const day = m[1].padStart(2, "0");
  const month = m[2].padStart(2, "0");
  const year = m[3];
  return `${year}-${month}-${day}`;
}

/** Parse European datetime (DD/MM/YYYY HH:mm) to ISO string; returns null if invalid/empty */
export function parseDateTimeEU(euDateTime: string): string | null {
  const trimmed = euDateTime.trim();
  if (!trimmed) return null;
  const m = trimmed.match(
    /^\s*(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})\s+(\d{1,2}):(\d{2})\s*$/,
  );
  if (!m) return null;
  const day = m[1].padStart(2, "0");
  const month = m[2].padStart(2, "0");
  const year = m[3];
  const hour = m[4].padStart(2, "0");
  const minute = m[5].padStart(2, "0");
  const date = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
  if (isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** Convert ISO string to European datetime (DD/MM/YYYY HH:mm) for display */
export function formatDateTimeEU(isoString: string | null | undefined): string {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
